<template>
  <div class="homepage">
    <!-- Hero Section -->
    <section class="hero">
      <div class="container">
        <div class="hero-grid">
          <div class="hero-content">
            <h1 class="hero-title">
              Build Software That
              <span class="hero-highlight"> Everyone </span>
              Can Use
            </h1>
            <p class="hero-subtitle">
              Custom software development and accessibility remediation services
              that drive growth while ensuring compliance.
            </p>
            <div class="hero-buttons">
              <a href="#contact" class="btn-primary">
                <span>Get a Free Accessibility Audit</span>
                <svg
                  class="icon-small"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </a>
              <a href="#services" class="btn-secondary">Learn More</a>
            </div>
          </div>
          <div class="hero-card-wrapper">
            <div class="hero-card">
              <div class="hero-card-content">
                <div
                  class="feature-item"
                  v-for="feature in heroFeatures"
                  :key="feature"
                >
                  <svg
                    class="icon-check"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>{{ feature }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section class="stats">
      <div class="container">
        <div class="stats-grid">
          <div v-for="stat in stats" :key="stat.label" class="stat-item">
            <div class="stat-number">{{ stat.number }}</div>
            <div class="stat-label">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Services Section -->
    <section id="services" class="services">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">Our Services</h2>
          <p class="section-subtitle">
            We build fast, scalable software and fix accessibility issues that
            cost you users and revenue
          </p>
        </div>
        <div class="services-grid">
          <div
            v-for="service in services"
            :key="service.title"
            class="service-card"
          >
            <div class="service-icon">
              <svg
                class="icon-large"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  v-if="service.icon === 'code'"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                />
                <path
                  v-else
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
            </div>
            <h3 class="service-title">{{ service.title }}</h3>
            <p class="service-description">{{ service.description }}</p>
            <ul class="service-features">
              <li
                v-for="feature in service.features"
                :key="feature"
                class="service-feature"
              >
                <svg
                  class="icon-check-small"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>{{ feature }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Benefits Section -->
    <section class="benefits">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">Why Choose Us</h2>
          <p class="section-subtitle">
            The advantages of working with our team
          </p>
        </div>
        <div class="benefits-grid">
          <div
            v-for="benefit in benefits"
            :key="benefit.title"
            class="benefit-item"
          >
            <div class="benefit-icon">
              <svg
                class="icon-medium"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  v-if="benefit.icon === 'zap'"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
                <path
                  v-else-if="benefit.icon === 'shield'"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
                <path
                  v-else
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h3 class="benefit-title">{{ benefit.title }}</h3>
            <p class="benefit-desc">{{ benefit.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Free Accessibility Audit Section -->
    <section id="audit" class="audit">
      <div class="container-small">
        <div class="section-header">
          <h2 class="section-title-white">Free Accessibility Audit</h2>
          <p class="section-subtitle-white">
            Get a detailed report of accessibility issues on your website within
            72 hours
          </p>
        </div>
        <!-- Success message -->
        <div class="toast-backdrop" :class="{ active: successMessage }"></div>

        <div
          class="success-toast"
          :class="{ active: successMessage }"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {{ successMessage }}
        </div>

        <div class="audit-form-wrapper">
          <div class="audit-form">
            <div class="form-group">
              <label class="form-label">Website URL</label>
              <input
                ref="websiteUrlInput"
                v-model="websiteUrl"
                type="url"
                placeholder="https://yourwebsite.com"
                class="form-input"
                :class="{ 'input-error': websiteUrlError }"
              />
              <p v-if="websiteUrlError" class="error-message">
                {{ websiteUrlError }}
              </p>
            </div>

            <div class="form-group">
              <label class="form-label">Your Email</label>
              <input
                ref="emailInput"
                v-model="email"
                type="email"
                placeholder="you@company.com"
                class="form-input"
                :class="{ 'input-error': emailError }"
              />
              <p v-if="emailError" class="error-message">
                {{ emailError }}
              </p>
            </div>
            <button
              @click="handleAccessibilityCheck"
              :disabled="isChecking"
              class="form-submit"
            >
              {{ isChecking ? "Analyzing..." : "Get Free Audit Report" }}
            </button>
            <p class="form-note">
              No credit card required. Report delivered via email in 72 hours.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Contact Section -->
    <section id="contact" class="contact">
      <div class="container-small">
        <h2 class="section-title">Ready to Get Started?</h2>
        <p class="section-subtitle">
          Let's discuss how we can help transform your digital presence
        </p>
        <div class="contact-buttons">
          <a href="mailto:support@accissi.com" class="btn-primary">Email Us</a>
          <!-- <a href="tel:+12675216510" class="btn-secondary">Schedule a Call</a> -->
          <a href="https://calendly.com/accissi/30min" class="btn-secondary"
            >Schedule a Call</a
          >
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { db } from "@/firebase/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

export default {
  name: "BodyPage",
  data() {
    return {
      websiteUrl: "",
      email: "",
      isChecking: false,

      websiteUrlError: "",
      emailError: "",

      successMessage: "",
      successTimeout: null,

      heroFeatures: [
        "WCAG Compliant & AI-assisted Accessibility",
        "VPAT Creation & Accessibility Documentation",
        "Modern AI-Powered Technology Stack",
        "Fast Delivery with AI-Enhanced Development",
        "24/7 Support & Proactive Monitoring",
      ],
      stats: [
        { number: "50+", label: "Projects Delivered" },
        { number: "98%", label: "Client Satisfaction" },
        { number: "24/7", label: "Support Available" },
        { number: "100%", label: "WCAG Compliant" },
      ],
      services: [
        {
          icon: "code",
          title: "Custom Software Development",
          description:
            "Designing and building secure, scalable, and high-performance software tailored to your unique business requirements.",
          features: [
            "Web Applications",
            "Mobile Applications",
            "API Development",
            "Custom Accessibility Testing Tools",
            "AI-Powered Monitoring System"
          ],
        },
        {
          icon: "eye",
          title: "ADA Compliance Support",
          description:
            "WCAG 2.2 compliant solutions that make your digital products accessible to everyone and legally compliant.",
          features: [
            "WCAG 2.2 Audits",
            "Screen Reader Testing",
            "Automation Testing & Validation",
            "AI-Powered Testing",
            "Accessibility Remediation"
          ],
        },
      ],
      benefits: [
        {
          icon: "zap",
          title: "Fast Turnaround",
          desc: "Projects delivered on time, every time",
        },
        {
          icon: "shield",
          title: "Risk Mitigation",
          desc: "Avoid costly ADA lawsuits with compliant code",
        },
        {
          icon: "users",
          title: "Expert Team",
          desc: "Senior developers with 14+ years experience",
        },
      ],
    };
  },

  methods: {
    async handleAccessibilityCheck() {
      this.websiteUrlError = "";
      this.emailError = "";

      const websiteUrl = this.websiteUrl.trim();
      const email = this.email.trim();

      // Website validation
      if (!websiteUrl) {
        this.websiteUrlError = "Please enter your website URL.";

        this.$nextTick(() => {
          this.$refs.websiteUrlInput?.focus();
        });

        return;
      }

      try {
        new URL(websiteUrl);
      } catch {
        this.websiteUrlError = "Please enter a valid website URL.";

        this.$nextTick(() => {
          this.$refs.websiteUrlInput?.focus();
        });

        return;
      }

      // Email validation
      if (!email) {
        this.emailError = "Please enter your email address.";

        this.$nextTick(() => {
          this.$refs.emailInput?.focus();
        });

        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailRegex.test(email)) {
        this.emailError = "Please enter a valid email address.";

        this.$nextTick(() => {
          this.$refs.emailInput?.focus();
        });

        return;
      }

      this.isChecking = true;

      try {
        await addDoc(collection(db, "accessibility-audits"), {
          websiteUrl,
          email,
          submittedAt: serverTimestamp(),
        });

        this.successMessage = `Thanks! We'll send a detailed accessibility report for ${websiteUrl} to ${email} within 72 hours.`;

        clearTimeout(this.successTimeout);

        this.successTimeout = setTimeout(() => {
          this.successMessage = "";
        }, 5000);

        this.websiteUrl = "";
        this.email = "";
      } catch (error) {
        console.error("Error submitting audit request:", error);
        alert("Oops! Something went wrong. Please try again.");
      } finally {
        this.isChecking = false;
      }
    },
  },
  beforeUnmount() {
    clearTimeout(this.successTimeout);
  },
};
</script>

<style scoped>
.toast-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(3px);

  opacity: 0;
  pointer-events: none;

  transition: opacity 0.25s ease;
  z-index: 9998;
}

.toast-backdrop.active {
  opacity: 1;
  pointer-events: auto;
}

/* ===== SUCCESS POPUP ===== */
.success-toast {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.9);

  z-index: 9999;

  width: min(92vw, 440px);
  padding: 22px 26px;

  background: linear-gradient(135deg, #16a34a, #22c55e);
  color: #fff;

  border-radius: 16px;
  box-shadow: 0 25px 70px rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(255, 255, 255, 0.1);

  font-size: 15.5px;
  font-weight: 700;
  text-align: center;
  line-height: 1.6;

  opacity: 0;
  pointer-events: none;

  transition: all 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}

.success-toast.active {
  opacity: 1;
  transform: translate(-50%, -50%) scale(1);
  pointer-events: auto;
}

/* end message */

.input-error {
  border: 2px solid #dc2626;
}

.error-message {
  margin-top: 6px;
  color: #dc2626;
  font-size: 14px;
  font-weight: 500;
}
.input-error {
  border: 2px solid #dc2626;
}

.error-message {
  margin-top: 6px;
  color: #dc2626;
  font-size: 14px;
  font-weight: 500;
}

/* ===== Reset & Base ===== */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
.feature-item,
.service-feature {
  text-align: left !important;
}

.homepage {
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Helvetica Neue", Arial, sans-serif;
  background: linear-gradient(to bottom right, #f8fafc, #e2e8f0);
}

.container,
.container-small {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
}

.icon-check {
  width: 1em;
  height: 1em;
  vertical-align: middle;
}

.service-feature {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.icon-check-small {
  width: 1em;
  height: 1em;
  vertical-align: middle;
}

/* ===== Icons ===== */
.icon {
  width: 24px;
  height: 24px;
}
.icon-small {
  width: 20px;
  height: 20px;
}
.icon-medium {
  width: 24px;
  height: 24px;
}
.icon-large {
  width: 32px;
  height: 32px;
}
.icon-check {
  width: 24px;
  height: 24px;
  color: #10b981;
}
.icon-check-small {
  width: 20px;
  height: 20px;
  color: #10b981;
  flex-shrink: 0;
}

/* ===== Navigation ===== */
.nav {
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 50;
  transition: all 0.3s;
  background: transparent;
}

.nav-scrolled {
  background: white;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.nav-container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
}
.nav-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.logo-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(to bottom right, #2563eb, #9333ea);
  border-radius: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
}
.logo-text {
  font-size: 1.5rem;
  font-weight: bold;
  background: linear-gradient(to right, #2563eb, #9333ea);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Desktop menu */
.nav-menu-desktop {
  display: none;
  gap: 2rem;
  align-items: center;
}
@media (min-width: 768px) {
  .nav-menu-desktop {
    display: flex;
  }
}

.nav-link {
  color: #334155;
  text-decoration: none;
  transition: color 0.3s;
}
.nav-link:hover {
  color: #2563eb;
}

.nav-btn {
  background: linear-gradient(to right, #2563eb, #9333ea);
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 9999px;
  text-decoration: none;
  transition: box-shadow 0.3s;
}
.nav-btn:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.2);
}

/* Mobile menu */
.nav-menu-mobile {
  display: block;
  background: none;
  border: none;
  cursor: pointer;
}
@media (min-width: 768px) {
  .nav-menu-mobile {
    display: none;
  }
}

.mobile-menu {
  background: white;
  border-top: 1px solid #e2e8f0;
}
.mobile-menu-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.5rem 1rem 1rem;
}
.mobile-link {
  color: #334155;
  text-decoration: none;
  padding: 0.5rem 0;
}
.mobile-link:hover {
  color: #2563eb;
}
.mobile-link-primary {
  font-weight: 600;
  color: #2563eb;
}

/* ===== Hero Section ===== */
.hero {
  padding-top: 100px; /* space for fixed nav */
  padding-bottom: 5rem;
}
@media (min-width: 768px) {
  .hero {
    padding-top: 120px;
  }
}

.hero-grid {
  display: grid;
  gap: 2rem;
  grid-template-columns: 1fr;
  align-items: center;
}
@media (min-width: 768px) {
  .hero-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.hero-title {
  font-size: 2rem;
  font-weight: bold;
  color: #0f172a;
  margin-bottom: 1rem;
  line-height: 1.2;
}
@media (min-width: 640px) {
  .hero-title {
    font-size: 2.5rem;
  }
}
@media (min-width: 768px) {
  .hero-title {
    font-size: 3.75rem;
  }
}

.hero-highlight {
  background: linear-gradient(to right, #2563eb, #9333ea);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-subtitle {
  font-size: 1rem;
  color: #475569;
  margin-bottom: 1.5rem;
}
@media (min-width: 640px) {
  .hero-subtitle {
    font-size: 1.25rem;
  }
}

.hero-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}
.btn-primary,
.btn-secondary {
  border-radius: 9999px;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: box-shadow 0.3s;
}

.btn-primary {
  background: linear-gradient(to right, #2563eb, #9333ea);
  color: white;
  padding: 0.75rem 1.5rem;
}
.btn-primary:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2);
}

.btn-secondary {
  background: white;
  color: #334155;
  border: 2px solid #e2e8f0;
  padding: 0.75rem 1.5rem;
}
.btn-secondary:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

/* Hero Card */
.hero-card-wrapper {
  position: relative;
}
.hero-card {
  background: linear-gradient(to bottom right, #3b82f6, #9333ea);
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}
.hero-card-content {
  background: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* ===== Stats Section ===== */
.stats {
  padding: 4rem 1rem;
  background: linear-gradient(to right, #2563eb, #9333ea);
}
.stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}
@media (min-width: 768px) {
  .stats-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}
.stat-item {
  text-align: center;
}
.stat-number {
  font-size: 2rem;
  font-weight: bold;
  color: white;
  margin-bottom: 0.5rem;
}
@media (min-width: 768px) {
  .stat-number {
    font-size: 3rem;
  }
}
.stat-label {
  color: #bfdbfe;
}
/* ===== SERVICES (IMPORTANT SECTION FIX) ===== */

.services {
  padding: 6rem 1rem;
}

/* 🔥 THIS FIXES YOUR "OVERLAPPING TITLE ISSUE" */
.section-header {
  text-align: center;
  max-width: 850px;
  margin: 0 auto 4.5rem;
  position: relative;
}

.section-title {
  font-size: 3rem;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 1rem;
  letter-spacing: -1px;
}

.section-title::after {
  content: "";
  display: block;
  width: 90px;
  height: 4px;
  margin: 1rem auto 0;
  border-radius: 999px;
  background: linear-gradient(90deg, #2563eb, #9333ea);
}

.section-subtitle {
  font-size: 1.2rem;
  color: #64748b;
  max-width: 650px;
  margin: 0 auto;
  line-height: 1.7;
}

.services-grid {
  display: grid;
  gap: 2rem;
}

@media (min-width: 768px) {
  .services-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.service-card {
  background: #fff;
  border-radius: 24px;
  padding: 2rem;
  border: 1px solid #e2e8f0;

  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  position: relative;
}

.service-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(37, 99, 235, 0.12);
}

.service-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}

.service-description {
  color: #475569;
  line-height: 1.7;
  margin-bottom: 1.5rem;
}

.service-feature {
  background: #f8fafc;
  padding: 0.6rem 1rem;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  font-size: 0.9rem;
}
/* ===== Benefits Section ===== */
.benefits {
  padding: 5rem 1rem;
  background: white;
}
.benefits-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
}
@media (min-width: 768px) {
  .benefits-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}
.benefit-item {
  text-align: center;
  padding: 1.5rem;
}
.benefit-icon {
  width: 64px;
  height: 64px;
  background: linear-gradient(to bottom right, #dbeafe, #e9d5ff);
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  margin: 0 auto 1rem;
}
.benefit-title {
  font-size: 1.25rem;
  font-weight: bold;
  color: #0f172a;
  margin-bottom: 0.5rem;
}
.benefit-desc {
  color: #475569;
}

/* ===== Audit Section ===== */
.audit {
  padding: 5rem 1rem;
  background: linear-gradient(to bottom right, #0f172a, #1e293b);
}
.section-title-white {
  font-size: 2.25rem;
  font-weight: bold;
  color: white;
  margin-bottom: 1rem;
}
.section-subtitle-white {
  font-size: 1.25rem;
  color: #cbd5e1;
}

.audit-form-wrapper {
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}
.audit-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.form-group {
  display: flex;
  flex-direction: column;
}
.form-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0.5rem;
}
.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 0.5rem;
  font-size: 1rem;
}
.form-input:focus {
  outline: none;
  border-color: #2563eb;
}
.form-submit {
  width: 100%;
  background: linear-gradient(to right, #2563eb, #9333ea);
  color: white;
  padding: 1rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
}
.form-submit:hover {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.2);
}
.form-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.form-note {
  font-size: 0.875rem;
  color: #64748b;
  text-align: center;
}

/* ===== Contact Section ===== */
.contact {
  padding: 5rem 1rem;
  text-align: center;
}
.contact-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
}
</style>
